import { getCustomBrandingConfig, getEnvInfo } from "../configurations";
import { CustomBrandingCdnResource } from "../types/customBranding";
import i18n, { Language } from "../i18n";
import log, { METRIC_NAME } from "../logging";

export const EMPTY_LOGO_GIF =
    "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

export const getCustomBrandingResourceCdnPath = (
    fileName: CustomBrandingCdnResource,
    extension: string
) => {
    const customBrandingConfig = getCustomBrandingConfig();

    if (customBrandingConfig?.userSettingId == null) {
        return;
    }

    const { stage, region } = getEnvInfo();
    const stagePrefix = stage.toLowerCase() === "prod" ? "" : `${stage}.`;
    const baseUrl = `https://${stagePrefix}cdn.workspaces-web.com/branding-${region}/${customBrandingConfig.userSettingId}/customer_assets`;

    return `${baseUrl}/${fileName}${extension}`;
};

const addOrUpdateTranslationMessage = (
    langCode: string,
    key: string,
    value: string
) => {
    i18n.addResourceBundle(
        langCode,
        "translation",
        { [key]: value },
        true,
        true
    );
};

/**
 * Initialize custom branding translations by adding them to i18n resources
 */
export const initializeCustomBrandingTranslations = () => {
    const customBrandingConfig = getCustomBrandingConfig();

    if (!customBrandingConfig?.localizedStrings) {
        return;
    }

    Object.entries(customBrandingConfig.localizedStrings).forEach(
        ([langCode, brandingStrings]) => {
            if (!brandingStrings) {
                return;
            }
            try {
                if (!i18n.hasResourceBundle(langCode, "translation")) {
                    return;
                }

                if (brandingStrings.browserTabTitle) {
                    addOrUpdateTranslationMessage(
                        langCode,
                        "branding.browserTabTitle",
                        brandingStrings.browserTabTitle
                    );
                }

                if (brandingStrings.welcomeText) {
                    addOrUpdateTranslationMessage(
                        langCode,
                        "euc.signIn.product.description",
                        brandingStrings.welcomeText
                    );
                }

                if (brandingStrings.loginTitle) {
                    addOrUpdateTranslationMessage(
                        langCode,
                        "euc.signIn.header.label",
                        brandingStrings.loginTitle
                    );
                }

                if (brandingStrings.loginDescription) {
                    addOrUpdateTranslationMessage(
                        langCode,
                        "euc.signIn.header.description",
                        brandingStrings.loginDescription
                    );
                }

                if (brandingStrings.loginButtonText) {
                    addOrUpdateTranslationMessage(
                        langCode,
                        "login.button.label",
                        brandingStrings.loginButtonText
                    );
                }

                if (brandingStrings.contactButtonText) {
                    addOrUpdateTranslationMessage(
                        langCode,
                        "euc.signIn.header.contactUs.label",
                        brandingStrings.contactButtonText
                    );
                }

                if (brandingStrings.loadingText) {
                    addOrUpdateTranslationMessage(
                        langCode,
                        "euc.loading.screen.connecting.message",
                        brandingStrings.loadingText
                    );
                    addOrUpdateTranslationMessage(
                        langCode,
                        "euc.loading.screen.reserving.message",
                        brandingStrings.loadingText
                    );
                }

                // We will hide contact-us button if the link is empty
                addOrUpdateTranslationMessage(
                    langCode,
                    "branding.contactLink",
                    brandingStrings.contactLink ?? ""
                );

                log.publishMetricWithDimensions(
                    METRIC_NAME.CUSTOM_BRANDING_ASSET_SUCCESS,
                    {
                        resource: "localizedBrandingMessages",
                        language: langCode,
                    }
                );
            } catch (error) {
                log.error(
                    "Failed to initialize custom branding translations:",
                    error
                );
                log.publishMetricWithDimensions(
                    METRIC_NAME.MISSING_CUSTOM_BRANDING_ASSET,
                    {
                        resource: "localizedBrandingMessages",
                        language: langCode,
                    }
                );
            }
        }
    );
};

/**
 * Get enabled languages from custom branding config
 */
export function getEnabledLanguages(): Set<Language> | undefined {
    const customBrandingConfig = getCustomBrandingConfig();
    return customBrandingConfig?.localizedStrings
        ? new Set(
              Object.keys(customBrandingConfig.localizedStrings) as Language[]
          )
        : undefined;
}
